import { useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { BoardColumn } from './BoardColumn'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { KanbanBoard } from '@/components/kanban'
import type { KanbanCardMoveEvent, KanbanColumnData } from '@/components/kanban'
import { projectBoardsService } from '@/features/projectBoards/services/projectBoards.service'
import { withErrorCode } from '@/lib/errors'
import type {
  ProjectBoardColumn,
  ProjectBoardDocument,
  ProjectTaskDocument,
} from '@/types'

type TaskWithId = ProjectTaskDocument & { id: string }

interface Props {
  boardId: string
  board: ProjectBoardDocument & { id: string }
  tasks: Array<TaskWithId>
  currentUserId: string
  onOpenTask: (taskId: string) => void
  onCreateTaskInColumn: (columnId: string) => void
  onReload: () => void | Promise<void>
}

export function BoardView({
  boardId,
  board,
  tasks,
  onOpenTask,
  onCreateTaskInColumn,
  onReload,
}: Props) {
  const sortedColumns = useMemo(
    () => [...board.columns].sort((a, b) => a.order - b.order),
    [board.columns]
  )

  // Group tasks per column (sorted ascending by order) and shape into KanbanColumnData.
  const kanbanColumns = useMemo<KanbanColumnData<TaskWithId>[]>(() => {
    return sortedColumns.map((col) => ({
      id: col.id,
      title: col.label,
      cards: tasks
        .filter((t) => t.columnId === col.id)
        .sort((a, b) => a.order - b.order),
    }))
  }, [sortedColumns, tasks])

  async function handleColumnReorder(orderedColumnIds: string[]) {
    const byId = new Map(board.columns.map((c) => [c.id, c] as const))
    const reordered: ProjectBoardColumn[] = orderedColumnIds
      .map((id, i) => {
        const col = byId.get(id)
        return col ? { ...col, order: i } : null
      })
      .filter((c): c is ProjectBoardColumn => c !== null)
    await projectBoardsService.updateBoardColumns(boardId, reordered)
    await onReload()
  }

  async function handleCardMove({ cardId, toColumnId, toIndex }: KanbanCardMoveEvent) {
    const activeTask = tasks.find((t) => t.id === cardId)
    if (!activeTask) return

    // Compute fractional newOrder from toIndex within the target column,
    // excluding the active card from the target list.
    const targetCards = kanbanColumns.find((c) => c.id === toColumnId)?.cards ?? []
    const withoutActive = targetCards.filter((c) => c.id !== cardId)

    let newOrder: number
    if (withoutActive.length === 0) {
      newOrder = 0
    } else if (toIndex <= 0) {
      newOrder = (withoutActive[0]?.order ?? 0) - 1
    } else if (toIndex >= withoutActive.length) {
      newOrder = (withoutActive[withoutActive.length - 1]?.order ?? 0) + 1
    } else {
      const prev = withoutActive[toIndex - 1]
      const next = withoutActive[toIndex]
      newOrder = ((prev?.order ?? 0) + (next?.order ?? 0)) / 2
    }

    if (activeTask.columnId === toColumnId && activeTask.order === newOrder) return

    await projectBoardsService.updateTask(boardId, cardId, {
      columnId: toColumnId,
      order: newOrder,
    })
    await onReload()
  }

  async function handleRenameColumn(columnId: string, newLabel: string) {
    const nextColumns = board.columns.map((c) =>
      c.id === columnId ? { ...c, label: newLabel } : c
    )
    await projectBoardsService.updateBoardColumns(boardId, nextColumns)
    await onReload()
  }

  async function handleDeleteColumn(columnId: string) {
    if (board.columns.length <= 1) {
      window.alert(withErrorCode('Cannot delete the last column.', 'project-board/last-column'))
      return
    }
    const remaining = board.columns.filter((c) => c.id !== columnId)
    const firstRemaining = [...remaining].sort((a, b) => a.order - b.order)[0]
    if (!firstRemaining) return

    // Move tasks from this column into the first remaining column.
    const targetTasks = tasks
      .filter((t) => t.columnId === firstRemaining.id)
      .sort((a, b) => a.order - b.order)
    let nextOrderInTarget =
      (targetTasks.length > 0
        ? Math.max(...targetTasks.map((t) => t.order))
        : -1) + 1
    const tasksToMove = tasks.filter((t) => t.columnId === columnId)
    for (const task of tasksToMove) {
      await projectBoardsService.updateTask(boardId, task.id, {
        columnId: firstRemaining.id,
        order: nextOrderInTarget++,
      })
    }

    const nextColumns: ProjectBoardColumn[] = remaining
      .sort((a, b) => a.order - b.order)
      .map((c, i) => ({ ...c, order: i }))
    await projectBoardsService.updateBoardColumns(boardId, nextColumns)
    await onReload()
  }

  async function handleAddColumn(label: string) {
    const trimmed = label.trim()
    if (!trimmed) return
    const maxOrder =
      board.columns.length > 0
        ? Math.max(...board.columns.map((c) => c.order))
        : -1
    const nextColumns: ProjectBoardColumn[] = [
      ...board.columns,
      { id: crypto.randomUUID(), label: trimmed, order: maxOrder + 1 },
    ]
    await projectBoardsService.updateBoardColumns(boardId, nextColumns)
    await onReload()
  }

  return (
    <KanbanBoard
      columns={kanbanColumns}
      onCardMove={handleCardMove}
      onColumnReorder={handleColumnReorder}
      renderColumn={(col) => (
        <BoardColumn
          key={col.id}
          column={col}
          board={board}
          onOpenTask={onOpenTask}
          onCreateTask={onCreateTaskInColumn}
          onRenameColumn={handleRenameColumn}
          onDeleteColumn={handleDeleteColumn}
        />
      )}
      trailingSlot={<AddColumnButton onAdd={handleAddColumn} />}
    />
  )
}

function AddColumnButton({ onAdd }: { onAdd: (label: string) => void | Promise<void> }) {
  const [adding, setAdding] = useState(false)
  const [label, setLabel] = useState('')

  async function submit() {
    if (!label.trim()) {
      setAdding(false)
      return
    }
    await onAdd(label)
    setLabel('')
    setAdding(false)
  }

  if (!adding) {
    return (
      <div className="flex w-72 shrink-0 items-start">
        <Button
          variant="outline"
          size="sm"
          className="w-full justify-start text-muted-foreground"
          onClick={() => setAdding(true)}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add column
        </Button>
      </div>
    )
  }

  return (
    <div className="flex w-72 shrink-0 flex-col gap-2 rounded-lg border bg-muted/30 p-2">
      <Input
        autoFocus
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            void submit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            setLabel('')
            setAdding(false)
          }
        }}
        placeholder="Column name"
        className="h-8 text-sm"
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void submit()}>
          Add
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setLabel('')
            setAdding(false)
          }}
        >
          Cancel
        </Button>
      </div>
    </div>
  )
}
